import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

const AuthCallbackPage = () => {
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [error, setError] = useState('');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { loginWithOAuth } = useAuth();

  useEffect(() => {
    const processOAuthCallback = async () => {
      const sessionId = searchParams.get('session_id');
      
      if (!sessionId) {
        setStatus('error');
        setError(t('auth.oauthError', 'OAuth session not found'));
        return;
      }

      try {
        // Exchange session_id for our JWT token
        const response = await fetch(`${API}/api/oauth/google/callback`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ session_id: sessionId }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'OAuth authentication failed');
        }

        const data = await response.json();
        
        // Store token and user data
        await loginWithOAuth(data.access_token, data.user);
        
        setStatus('success');
        
        if (data.is_new_user) {
          toast.success(t('auth.welcomeNewUser', 'Welcome! Your account has been created.'));
        } else {
          toast.success(t('auth.loginSuccess', 'Login successful!'));
        }
        
        // Redirect to home after a brief delay
        setTimeout(() => {
          navigate('/');
        }, 1500);
        
      } catch (err) {
        console.error('OAuth callback error:', err);
        setStatus('error');
        setError(err.message || t('auth.oauthError', 'Authentication failed'));
      }
    };

    processOAuthCallback();
  }, [searchParams, navigate, t, loginWithOAuth]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md" data-testid="oauth-callback-card">
        <CardContent className="flex flex-col items-center justify-center py-12">
          {status === 'processing' && (
            <>
              <Loader2 className="w-12 h-12 text-amber-600 animate-spin mb-4" />
              <h2 className="text-xl font-semibold text-amber-900 mb-2">
                {t('auth.processingLogin', 'Processing login...')}
              </h2>
              <p className="text-amber-600 text-center">
                {t('auth.pleaseWait', 'Please wait while we verify your account.')}
              </p>
            </>
          )}
          
          {status === 'success' && (
            <>
              <CheckCircle className="w-12 h-12 text-green-600 mb-4" />
              <h2 className="text-xl font-semibold text-amber-900 mb-2">
                {t('auth.loginSuccess', 'Login successful!')}
              </h2>
              <p className="text-amber-600 text-center">
                {t('auth.redirecting', 'Redirecting you to the homepage...')}
              </p>
            </>
          )}
          
          {status === 'error' && (
            <>
              <XCircle className="w-12 h-12 text-red-500 mb-4" />
              <h2 className="text-xl font-semibold text-amber-900 mb-2">
                {t('auth.loginFailed', 'Login failed')}
              </h2>
              <p className="text-red-500 text-center mb-4">
                {error}
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => navigate('/login')}
                  data-testid="back-to-login-btn"
                >
                  {t('auth.backToLogin', 'Back to Login')}
                </Button>
                <Button
                  onClick={() => window.location.reload()}
                  data-testid="retry-btn"
                >
                  {t('common.retry', 'Retry')}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuthCallbackPage;
